import React, { useMemo } from "react";
import toLocalTime from "utils/toLocalTime";
import { useEffect } from "react";
// removed unused react-redux imports; user is sourced via React Query cache
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "components/ui/accordion";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faLock,
  faDesktop,
  faCog,
  faList,
  faFilter,
  faCheckCircle,
  faMinusCircle,
  faExclamationTriangle,
  faChevronDown,
} from "@fortawesome/free-solid-svg-icons";
import { Link } from "react-router-dom";
import { Spinner } from "./shared/Spinner";
import { SiteStatus } from "./SiteStatus";
import useGetSitesStatus, { sitesStatus } from "./hooks/useGetSitesStatus";
import useDebounce from "utils/useDebounce";
import { UseLivlyLeaseUser } from "shared/api/LivlyUserProvider";
import { Button } from "components/ui/button";
import { Input } from "components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "components/ui/popover";
import { RadioGroup, RadioGroupItem } from "components/ui/radio-group";

// Create a simple replacement for Glyphicon
const Glyphicon = ({
  glyph,
  style,
  className,
}: {
  glyph: string;
  style?: React.CSSProperties;
  className?: string;
}) => {
  const iconMap: { [key: string]: any } = {
    lock: faLock,
    "modal-window": faDesktop,
    cog: faCog,
    list: faList,
    filter: faFilter,
    "ok-sign": faCheckCircle,
    "minus-sign": faMinusCircle,
    "warning-sign": faExclamationTriangle,
    "chevron-down": faChevronDown,
  };

  return (
    <FontAwesomeIcon
      icon={iconMap[glyph] || faLock}
      style={style}
      className={className}
    />
  );
};

// eslint-disable-next-line react-refresh/only-export-components
export enum SiteStatusEnum {
  Offline = "Offline",
  Unknown = "Unknown",
  Online = "Online",
}

type SiteStatusEnumType = (typeof SiteStatusEnum)[keyof typeof SiteStatusEnum];

export default function Home() {
  // user is fetched via route loader and stored in React Query cache
  const user = UseLivlyLeaseUser();

  const { data = [], isLoading } = useGetSitesStatus();
  const [filter, setFilter] = React.useState("");
  const [fuzzySearch, setFuzzySearch] = React.useState("");
  // Default view: only devices that need attention (non-Online). Users can opt into seeing everything.
  const [showAllStatuses, setShowAllStatuses] = React.useState(false);

  const [filterOpen, setFilterOpen] = React.useState(false);

  const handleClose = () => {
    setFilterOpen(false);
  };

  const handleChange = (event: SiteStatusEnumType) => {
    setFilter(event);
  };

  let timeOfDay;
  const date = toLocalTime(Date.now());

  const hours = date.hour();
  if (hours < 12) {
    timeOfDay = "Morning";
  } else if (hours >= 12 && hours < 17) {
    timeOfDay = "Afternoon";
  } else {
    timeOfDay = "Evening";
  }

  const debouncedSearch = useDebounce(fuzzySearch, 500);

  const filteredUsers = useMemo(() => {
    if (debouncedSearch !== "") {
      let search = debouncedSearch.toLowerCase();
      return data.filter(
        (data) =>
          data.name.toLowerCase().match(search) ||
          data.siteName.toLowerCase().match(search),
      );
    }

    if (filter !== "") {
      return data.filter((data) => data.status === filter);
    }

    if (!showAllStatuses) {
      return data.filter((data) => data.status !== SiteStatusEnum.Online);
    }

    return data;
  }, [data, filter, debouncedSearch, showAllStatuses]);

  type GroupByObject = { [key: string]: sitesStatus[] };

  const groppeddata = useMemo(() => {
    return filteredUsers.reduce((acc: GroupByObject, curr: sitesStatus) => {
      (acc[curr["siteName"]] = acc[curr["siteName"]] || []).push(curr);
      return acc;
    }, {});
  }, [filteredUsers]);

  const navItems = [
    {
      to: "/admin/sites",
      icon: "table-columns" as const,
      label: "Sites",
      description: "Manage all connected sites",
      gradient: "from-blue-500 to-blue-600",
      softBg: "bg-blue-50",
      softText: "text-blue-600",
      border: "border-blue-100",
      ring: "hover:ring-blue-200",
    },
    {
      to: "/admin/accessControlGroups",
      icon: "universal-access" as const,
      label: "Access Control Groups",
      description: "Configure access permissions",
      gradient: "from-violet-500 to-violet-600",
      softBg: "bg-violet-50",
      softText: "text-violet-600",
      border: "border-violet-100",
      ring: "hover:ring-violet-200",
    },
    {
      to: "/admin/locks",
      icon: "lock" as const,
      label: "Locks",
      description: "Monitor and control door locks",
      gradient: "from-indigo-500 to-indigo-600",
      softBg: "bg-indigo-50",
      softText: "text-indigo-600",
      border: "border-indigo-100",
      ring: "hover:ring-indigo-200",
    },
    {
      to: "/admin/keyRequests",
      icon: "clipboard" as const,
      label: "Access Logs",
      description: "View entry and activity history",
      gradient: "from-sky-500 to-sky-600",
      softBg: "bg-sky-50",
      softText: "text-sky-600",
      border: "border-sky-100",
      ring: "hover:ring-sky-200",
    },
  ];

  return (
    <div className="space-y-6">
      {/* Greeting banner */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-white to-sky-200 border border-sky-100 px-8 py-6 shadow-sm">
        {/* Decorative blobs */}
        <div className="absolute -top-8 -right-8 w-40 h-40 bg-blue-100/60 rounded-full blur-2xl" />
        <div className="absolute bottom-0 right-32 w-28 h-28 bg-indigo-100/50 rounded-full blur-2xl" />
        <div className="relative">
          <span className="inline-flex items-center gap-1.5 text-[10px] font-bold text-blue-500 uppercase tracking-widest mb-2">
            <span className="w-1.5 h-1.5 rounded-full bg-blue-400 animate-pulse" />
            Good {timeOfDay}
          </span>
          <h1 className="text-2xl md:text-3xl font-extrabold text-gray-900 tracking-tight">
            {user?.name} <span className="text-2xl">👋</span>
          </h1>
          <p className="mt-1.5 text-gray-400 text-sm">
            Here's an overview of your properties and access controls.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
        {/* Main content */}
        <div className="lg:col-span-9 space-y-5">
          {/* Nav cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {navItems.map(
              ({
                to,
                icon,
                label,
                description,
                gradient,
                softBg,
                softText,
                border,
                ring,
              }) => (
                <Link
                  key={to}
                  to={to}
                  className={`group relative flex flex-col rounded-2xl border ${border} shadow-sm p-5 ring-2 ring-transparent ${ring} hover:shadow-md hover:-translate-y-0.5 transition-all duration-200 overflow-hidden bg-gradient-to-br from-sky-50 via-white to-indigo-50`}
                >
                  {/* Subtle corner glow */}
                  <div
                    className={`absolute -top-4 -right-4 w-16 h-16 rounded-full bg-gradient-to-br ${gradient} opacity-10 group-hover:opacity-20 transition-opacity blur-md`}
                  />

                  <div
                    className={`w-11 h-11 rounded-xl bg-gradient-to-br ${gradient} flex items-center justify-center mb-3 shadow`}
                  >
                    <FontAwesomeIcon
                      icon={icon}
                      className="text-base text-white"
                    />
                  </div>

                  <p className="text-sm font-bold text-gray-900 leading-snug">
                    {label}
                  </p>
                  <p className="text-[11px] text-gray-400 mt-1 leading-relaxed">
                    {description}
                  </p>

                  <div
                    className={`mt-4 flex items-center gap-1 text-[11px] font-semibold ${softText}`}
                  >
                    Open
                    <FontAwesomeIcon
                      icon="arrow-right"
                      className="text-[9px] group-hover:translate-x-0.5 transition-transform"
                    />
                  </div>
                </Link>
              ),
            )}
          </div>

          {/* Site Status panel */}
          <div className="rounded-2xl overflow-hidden shadow-sm bg-gradient-to-br from-sky-50 via-white to-indigo-50 border border-sky-100">
              {/* Header */}
              <div className="relative bg-gradient-to-r from-white to-sky-100 border-b border-sky-100 px-6 py-5">
                <div className="absolute -top-8 -right-8 w-40 h-40 bg-blue-100/30 rounded-full blur-2xl" />
                <div className="relative flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-sky-600 rounded-lg flex items-center justify-center">
                      <Glyphicon glyph="cog" className="text-white text-xs" />
                    </div>
                    <h2 className="text-sm font-bold text-gray-900">
                      {showAllStatuses ? "Site Status" : "Warnings"}
                    </h2>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      onClick={() => setShowAllStatuses(!showAllStatuses)}
                      className="text-[11px]"
                      size="xs"
                      type="button"
                      variant="ghost"
                    >
                      {showAllStatuses ? "Show warnings only" : "Show all sites"}
                    </Button>
                    <Popover
                      open={filterOpen}
                      onOpenChange={setFilterOpen}
                    >
                      <PopoverTrigger asChild>
                        <Button
                          aria-label="Filter site status"
                          className="border-gray-200 bg-gray-50 hover:bg-gray-100"
                          size="icon-xs"
                          type="button"
                          variant="outline"
                        >
                          <Glyphicon
                            glyph="filter"
                            className="text-gray-500 text-xs"
                          />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent align="end" className="w-auto min-w-[150px] p-2">
                        <div className="p-2 flex flex-col gap-0.5 min-w-[150px]">
                          <RadioGroup
                            className="gap-0.5"
                            value={filter}
                            onValueChange={(value) =>
                              handleChange(value as SiteStatusEnumType)
                            }
                          >
                            {[
                              SiteStatusEnum.Online,
                              SiteStatusEnum.Offline,
                              SiteStatusEnum.Unknown,
                            ].map((val) => (
                              <label
                                className="flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 hover:bg-sky-50"
                                htmlFor={`site-status-filter-${val}`}
                                key={val}
                              >
                                <RadioGroupItem
                                  id={`site-status-filter-${val}`}
                                  value={val}
                                />
                                <span className="text-sm text-gray-700">
                                  {val}
                                </span>
                              </label>
                            ))}
                          </RadioGroup>
                          <div className="mt-1.5 pt-1.5 border-t border-gray-200 flex justify-center">
                            <Button
                              onClick={() => {
                                setFilter("");
                                handleClose();
                              }}
                              className="h-auto py-1 text-[11px]"
                              size="xs"
                              type="button"
                              variant="ghost"
                            >
                              Clear
                            </Button>
                          </div>
                        </div>
                      </PopoverContent>
                    </Popover>
                    <Input
                      aria-label="Search sites"
                      placeholder="Search sites…"
                      className="h-8 bg-gray-50 text-xs"
                      onChange={(event) => setFuzzySearch(event.target.value)}
                    />
                  </div>
                </div>
              </div>

              {/* Content */}
              <div className="p-6 space-y-4">
                {isLoading && <Spinner />}

                {filteredUsers.length > 0 &&
                  Object.keys(groppeddata).map((siteGroupName, i) => (
                    <div key={i}>
                      {/* Site group */}
                      <div className="mb-3">
                        <div className="flex items-center justify-between mb-4 px-0.5">
                          <p className="text-[11px] font-bold text-gray-700 uppercase tracking-wider">
                            {siteGroupName}
                          </p>
                          <div className="flex items-center gap-1.5">
                            <span className="flex items-center gap-1 text-[10px] font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded-full">
                              <span className="w-1 h-1 rounded-full bg-emerald-500" />{" "}
                              Online
                            </span>
                            <span className="flex items-center gap-1 text-[10px] font-semibold text-rose-700 bg-rose-50 border border-rose-200 px-2 py-0.5 rounded-full">
                              <span className="w-1 h-1 rounded-full bg-rose-500" />{" "}
                              Offline
                            </span>
                            <span className="flex items-center gap-1 text-[10px] font-semibold text-amber-700 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded-full">
                              <span className="w-1 h-1 rounded-full bg-amber-500" />{" "}
                              Unknown
                            </span>
                          </div>
                        </div>

                        {/* Sites grid - side by side */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          {groppeddata[siteGroupName].map(
                            (newStatus, index) => (
                              <div key={index}>
                                <div className="bg-white border border-sky-50 rounded-lg p-3 hover:border-sky-100 hover:shadow-sm transition-all text-xs">
                                  <SiteStatus siteStatusData={newStatus} />
                                </div>
                                {newStatus?.children &&
                                  newStatus.children.length > 0 && (
                                    <div className="mt-2">
                                      <Accordion type="single" collapsible>
                                        <AccordionItem className="rounded-md border border-sky-50 bg-[#fafbfd]" value="children">
                                          <AccordionTrigger className="min-h-8 px-1.5 py-0 hover:no-underline">
                                          <p className="text-[10px] font-semibold text-gray-600">
                                            Child sites (
                                            {newStatus.children.length})
                                          </p>
                                          </AccordionTrigger>
                                          <AccordionContent className="px-1.5 pt-1 pb-1.5">
                                          <div className="flex flex-col w-full gap-1.5">
                                            {newStatus.children.map(
                                              (childStatus) => (
                                                <div
                                                  key={childStatus.name}
                                                  className="bg-white border border-sky-50 rounded p-2.5 text-[11px]"
                                                >
                                                  <SiteStatus
                                                    siteStatusData={childStatus}
                                                  />
                                                </div>
                                              ),
                                            )}
                                          </div>
                                          </AccordionContent>
                                        </AccordionItem>
                                      </Accordion>
                                    </div>
                                  )}
                              </div>
                            ),
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
              </div>
          </div>
        </div>

        {/* Profile sidebar */}
        <div className="lg:col-span-3">
          <div className="bg-gradient-to-b from-sky-50 to-indigo-50 rounded-2xl border border-sky-100 shadow-sm overflow-hidden">
            {/* Header */}
            <div className="relative bg-gradient-to-br from-sky-50 to-indigo-50 border-b border-sky-100 px-5 py-5 overflow-hidden">
              <div className="absolute -top-6 -right-6 w-20 h-20 bg-blue-100/60 rounded-full blur-xl" />
              <div className="relative">
                <p className="text-[10px] font-bold text-blue-400 uppercase tracking-widest mb-3">
                  Profile
                </p>
                <div className="flex items-center gap-3">
                  <div
                    aria-hidden="true"
                    className="flex size-[42px] items-center justify-center rounded-full border-2 border-blue-200 bg-blue-100 text-[17px] font-extrabold text-blue-700"
                  >
                    {user?.name?.[0]}
                  </div>
                  <div>
                    <p className="text-sm font-bold text-gray-900 leading-tight">
                      {user?.name}
                    </p>
                    {user?.superUser && (
                      <span className="inline-flex items-center gap-1 mt-1 text-[10px] font-semibold bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full border border-blue-200">
                        <span className="w-1 h-1 rounded-full bg-emerald-500" />
                        Super Admin
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Info rows */}
            <div className="px-4 py-4 space-y-2.5">
              <a
                href={`mailto:${user?.email}`}
                className="flex items-center gap-3 group"
              >
                <div className="w-8 h-8 bg-blue-50 rounded-lg flex items-center justify-center shrink-0 group-hover:bg-blue-100 transition-colors">
                  <FontAwesomeIcon
                    icon="envelope"
                    className="text-[11px] text-blue-500"
                  />
                </div>
                <span className="text-xs text-gray-500 group-hover:text-blue-600 transition-colors truncate">
                  {user?.email}
                </span>
              </a>
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-blue-50 rounded-lg flex items-center justify-center shrink-0">
                  <FontAwesomeIcon
                    icon="user-gear"
                    className="text-[11px] text-blue-500"
                  />
                </div>
                <span className="text-xs text-gray-500">
                  Super Admin:{" "}
                  <span className="font-bold text-gray-800">
                    {user?.superUser ? "Yes" : "No"}
                  </span>
                </span>
              </div>
            </div>

            {/* Divider */}
            <div className="mx-4 h-px bg-gray-100" />

            {/* Follow */}
            <div className="px-4 pb-4 flex justify-center">
              <p className="text-[11px] text-gray-400">
                Follow us:{" "}
                <a
                  href="https://twitter.com/LivlyConnect"
                  rel="noopener"
                  className="text-blue-500 hover:text-blue-700 transition-colors font-semibold"
                >
                  @LivlyConnect
                </a>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
